import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/public/Navbar";
import { Footer } from "@/components/public/Footer";
import { WhatsAppButton } from "@/components/public/WhatsAppButton";
import { CategoryGallery } from "@/components/public/CategoryGallery";
import { useSettings } from "@/context/SettingsContext";
import api, { photoUrl, imageById } from "@/lib/api";
import { toast } from "sonner";
import { Star, ArrowRight, Check, Instagram, Send, Phone } from "lucide-react";

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`py-16 sm:py-24 lg:py-32 ${className}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">{children}</div>
  </section>
);

const Overline = ({ children }) => <p className="overline-label mb-4">{children}</p>;

export default function HomePage() {
  const settings = useSettings();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);

  const [galleryCat, setGalleryCat] = useState(null);

  const loadAll = useCallback(() => {
    api.get("/categories").then(({ data }) => {
      setCategories(data.filter((c) => c.photo_count > 0));
    }).catch(() => {});
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
    api.get("/packages").then(({ data }) => setPackages(data)).catch(() => {});
    api.get("/testimonials").then(({ data }) => setTestimonials(data)).catch(() => {});
    api.get("/faqs").then(({ data }) => setFaqs(data)).catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const tick = () => loadAll();
    const iv = setInterval(tick, 15000);
    const onVis = () => { if (!document.hidden) tick(); };
    window.addEventListener("focus", tick);
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); window.removeEventListener("focus", tick); document.removeEventListener("visibilitychange", onVis); };
  }, [loadAll]);

  const hero = settings?.hero || {};
  const about = settings?.about || {};
  const texts = settings?.texts || {};
  const contact = settings?.contact || {};

  const heroSlides = useMemo(() => {
    const manual = Array.isArray(hero.slides) ? hero.slides.filter(Boolean) : [];
    const covers = categories.map((c) => c.cover_url || (c.cover_photo_id ? imageById(c.cover_photo_id) : null)).filter(Boolean);
    const unique = Array.from(new Set([...manual, ...covers]));
    return unique.length ? unique : (hero.image_url ? [hero.image_url] : []);
  }, [hero.slides, hero.image_url, categories]);
  const [slide, setSlide] = useState(0);
  useEffect(() => { setSlide(0); }, [heroSlides.length]);
  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), 5000);
    return () => clearInterval(t);
  }, [heroSlides.length]);

  return (
    <div id="inicio">
      <Navbar />

      {/* HERO */}
      <div className="relative min-h-screen flex items-center overflow-hidden bg-ink">
        {heroSlides.map((src, i) => (
          <img
            key={src + i}
            src={src}
            alt="Yocaira Abreu Photography"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ${i === slide ? "opacity-100" : "opacity-0"}`}
          />
        ))}
        <div className="absolute inset-0 hero-scrim" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 w-full">
          <div className="max-w-2xl animate-fade-up">
            <p className="overline-label mb-6 text-gold-light">Fotografía profesional</p>
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl text-white font-medium leading-[1.05] tracking-tight">
              {hero.title}
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-white/85 font-light max-w-lg leading-relaxed">
              {hero.subtitle}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                data-testid="hero-portfolio-btn"
                onClick={() => document.querySelector("#portafolio")?.scrollIntoView({ behavior: "smooth" })}
                className="px-8 py-4 rounded-full bg-cream text-ink font-medium tracking-wide hover:bg-gold transition-all duration-300 flex items-center justify-center gap-2 group"
              >
                {hero.btn1_text || "Ver portafolio"}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                data-testid="hero-booking-btn"
                onClick={() => navigate(hero.btn2_link || "/reservar")}
                className="px-8 py-4 rounded-full border border-cream/60 text-cream font-medium tracking-wide hover:bg-cream hover:text-ink transition-all duration-300"
              >
                {hero.btn2_text || "Reservar una sesión"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PORTFOLIO */}
      <Section id="portafolio" className="bg-cream">
        <div className="text-center max-w-2xl mx-auto">
          <Overline>Galería</Overline>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink">{texts.portfolio_title || "Portafolio"}</h2>
          <p className="mt-4 text-ink-soft">{texts.portfolio_subtitle}</p>
        </div>

        {categories.length === 0 ? (
          <p className="text-center text-ink-muted mt-16">Aún no hay fotografías publicadas.</p>
        ) : (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((c) => (
              <button
                key={c.id}
                data-testid={`portfolio-cat-card-${c.id}`}
                onClick={() => setGalleryCat(c)}
                className="group relative block w-full aspect-[4/5] rounded-sm overflow-hidden bg-beige card-hover text-left"
              >
                {c.cover_photo_id ? (
                  <img
                    src={imageById(c.cover_photo_id, true)}
                    alt={c.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : (
                  <div className="absolute inset-0 bg-ink/80" />
                )}
                <span className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/25 to-transparent" />
                <span className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="block font-serif text-2xl text-cream">{c.name}</span>
                  <span className="mt-1 flex items-center gap-2 text-cream/80 text-sm">
                    <span>{c.photo_count} {c.photo_count === 1 ? "foto" : "fotos"}</span>
                    <span className="opacity-60">·</span>
                    <span className="inline-flex items-center gap-1 group-hover:text-gold transition-colors">
                      Ver galería <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </Section>

      {/* SERVICES */}
      <Section id="servicios" className="bg-cream-muted">
        <div className="text-center max-w-2xl mx-auto">
          <Overline>Lo que ofrezco</Overline>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink">{texts.services_title || "Servicios"}</h2>
          <p className="mt-4 text-ink-soft">{texts.services_subtitle}</p>
        </div>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((s) => (
            <div key={s.id} data-testid={`service-card-${s.id}`} className="bg-cream rounded-sm overflow-hidden card-hover border border-beige">
              <div className="img-zoom aspect-[4/3] bg-beige">
                {s.image_url || s.photo_id ? (
                  <img src={s.image_url || photoUrl({ id: s.photo_id }, true)} alt={s.name} loading="lazy" className="w-full h-full object-cover" />
                ) : null}
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl text-ink">{s.name}</h3>
                <p className="mt-2 text-sm text-ink-soft leading-relaxed">{s.description}</p>
                {s.starting_price ? (
                  <p className="mt-3 font-mono text-xs text-gold">Desde {s.starting_price}</p>
                ) : (
                  <p className="mt-3 font-mono text-xs text-ink-muted">Consultar precio</p>
                )}
                <div className="mt-5 flex gap-3">
                  <button
                    data-testid={`service-details-${s.id}`}
                    onClick={() => document.querySelector("#paquetes")?.scrollIntoView({ behavior: "smooth" })}
                    className="text-sm font-medium text-ink hover:text-gold transition-colors flex items-center gap-1"
                  >
                    Ver detalles <ArrowRight size={14} />
                  </button>
                  <button
                    data-testid={`service-book-${s.id}`}
                    onClick={() => navigate(`/reservar?session=${encodeURIComponent(s.name)}`)}
                    className="ml-auto px-4 py-2 rounded-full bg-ink text-cream text-sm hover:bg-gold hover:text-ink transition-all"
                  >
                    Reservar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* PACKAGES */}
      <Section id="paquetes" className="bg-cream">
        <div className="text-center max-w-2xl mx-auto">
          <Overline>Experiencias</Overline>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink">{texts.packages_title || "Paquetes"}</h2>
          <p className="mt-4 text-ink-soft">{texts.packages_subtitle}</p>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
          {packages.map((p, idx) => (
            <div
              key={p.id}
              data-testid={`package-card-${p.id}`}
              className={`rounded-sm p-8 flex flex-col card-hover border ${
                idx === 1 ? "bg-ink text-cream border-ink" : "bg-cream border-beige-dark"
              }`}
            >
              <h3 className={`font-serif text-3xl ${idx === 1 ? "text-cream" : "text-ink"}`}>{p.name}</h3>
              <p className={`mt-2 font-mono text-lg ${idx === 1 ? "text-gold-light" : "text-gold"}`}>{p.price || "Consultar precio"}</p>
              <ul className={`mt-6 space-y-3 text-sm flex-1 ${idx === 1 ? "text-cream/80" : "text-ink-soft"}`}>
                {p.duration && <li className="flex gap-2"><Check size={16} className="text-gold shrink-0 mt-0.5" />{p.duration}</li>}
                {p.edited_photos && <li className="flex gap-2"><Check size={16} className="text-gold shrink-0 mt-0.5" />{p.edited_photos}</li>}
                {p.outfit_changes && <li className="flex gap-2"><Check size={16} className="text-gold shrink-0 mt-0.5" />{p.outfit_changes}</li>}
                {p.location && <li className="flex gap-2"><Check size={16} className="text-gold shrink-0 mt-0.5" />{p.location}</li>}
                {(p.includes || []).map((inc, i) => (
                  <li key={i} className="flex gap-2"><Check size={16} className="text-gold shrink-0 mt-0.5" />{inc}</li>
                ))}
                {p.delivery_time && <li className="flex gap-2"><Check size={16} className="text-gold shrink-0 mt-0.5" />Entrega: {p.delivery_time}</li>}
              </ul>
              {p.cancellation_policy && (
                <p className={`mt-4 text-xs ${idx === 1 ? "text-cream/50" : "text-ink-muted"}`}>{p.cancellation_policy}</p>
              )}
              <button
                data-testid={`package-book-${p.id}`}
                onClick={() => navigate(`/reservar?package=${p.id}`)}
                className={`mt-6 px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  idx === 1 ? "bg-gold text-ink hover:bg-cream" : "bg-ink text-cream hover:bg-gold hover:text-ink"
                }`}
              >
                Reservar este paquete
              </button>
            </div>
          ))}
        </div>
      </Section>

      {/* ABOUT */}
      <Section id="sobre-mi" className="bg-cream-muted">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="img-zoom rounded-sm overflow-hidden aspect-[4/5] bg-beige order-1 lg:order-none">
            {about.image_url && <img src={about.image_url} alt="Yocaira Abreu" className="w-full h-full object-cover" />}
          </div>
          <div>
            <Overline>Sobre mí</Overline>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink gold-underline inline-block">{about.title || "Sobre mí"}</h2>
            <p className="mt-8 text-lg text-ink-soft leading-relaxed font-light">{about.text}</p>
            <button
              data-testid="about-booking-btn"
              onClick={() => navigate("/reservar")}
              className="mt-8 px-8 py-4 rounded-full bg-ink text-cream font-medium hover:bg-gold hover:text-ink transition-all duration-300"
            >
              Reservar una sesión
            </button>
          </div>
        </div>
      </Section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <Section id="testimonios" className="bg-ink">
          <div className="text-center max-w-2xl mx-auto">
            <Overline>Testimonios</Overline>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-cream">{texts.testimonials_title || "Testimonios"}</h2>
            <p className="mt-4 text-cream/60">{texts.testimonials_subtitle}</p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t) => (
              <div key={t.id} data-testid={`testimonial-${t.id}`} className="bg-charcoal/60 border border-white/10 rounded-sm p-8">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={16} className={i < t.rating ? "fill-gold text-gold" : "text-white/20"} />
                  ))}
                </div>
                <p className="font-serif text-lg text-cream/90 leading-relaxed italic">"{t.comment}"</p>
                <div className="mt-6 flex items-center gap-3">
                  {(t.image_url || t.photo_id) && (
                    <img src={t.image_url || photoUrl({ id: t.photo_id }, true)} alt={t.client_name} className="w-10 h-10 rounded-full object-cover" />
                  )}
                  <div>
                    <p className="text-cream font-medium text-sm">{t.client_name}</p>
                    <p className="text-gold text-xs font-mono">{t.session_type}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* CONTACT */}
      <ContactSection contact={contact} faqs={faqs} />

      <Footer />
      <WhatsAppButton />

      {galleryCat && (
        <CategoryGallery category={galleryCat} onClose={() => setGalleryCat(null)} />
      )}
    </div>
  );
}

const ContactSection = ({ contact, faqs }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Completa los campos obligatorios.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("¡Mensaje enviado! Te responderé muy pronto.");
      setForm({ name: "", email: "", phone: "", event_type: "", message: "" });
    } catch {
      toast.error("No se pudo enviar el mensaje. Intenta de nuevo.");
    } finally {
      setSending(false);
    }
  };

  const wa = contact.whatsapp ? `https://wa.me/${contact.whatsapp.replace(/\D/g, "")}` : null;

  return (
    <Section id="contacto" className="bg-cream">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <Overline>Hablemos</Overline>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink">Contacto</h2>
          <p className="mt-4 text-ink-soft">Cuéntame tu idea y creemos juntas algo inolvidable.</p>

          <div className="mt-8 space-y-4">
            {contact.phone && (
              <a data-testid="contact-phone-btn" href={`tel:${contact.phone.replace(/[^\d+]/g, "")}`} className="flex items-center gap-3 text-ink hover:text-gold transition-colors">
                <span className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center text-gold"><Phone size={18} /></span>
                {contact.phone}
              </a>
            )}
            {wa && (
              <a data-testid="contact-whatsapp-btn" href={wa} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-ink hover:text-gold transition-colors">
                <span className="w-10 h-10 rounded-full bg-[#25D366]/10 flex items-center justify-center text-[#25D366]"><Send size={18} /></span>
                WhatsApp directo
              </a>
            )}
            {contact.instagram && (
              <a data-testid="contact-instagram-btn" href={`https://instagram.com/${contact.instagram}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-ink hover:text-gold transition-colors">
                <span className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center text-gold"><Instagram size={18} /></span>
                @{contact.instagram}
              </a>
            )}
          </div>

          {contact.address && <p className="mt-8 text-sm text-ink-muted"><strong className="text-ink">Ubicación:</strong> {contact.address}</p>}
          {contact.service_area && <p className="mt-1 text-sm text-ink-muted"><strong className="text-ink">Área de servicio:</strong> {contact.service_area}</p>}
          {contact.hours && <p className="mt-1 text-sm text-ink-muted"><strong className="text-ink">Horario:</strong> {contact.hours}</p>}

          {faqs.length > 0 && (
            <div className="mt-10">
              <p className="overline-label mb-4">Preguntas frecuentes</p>
              <div className="space-y-4">
                {faqs.map((f) => (
                  <div key={f.id} className="border-b border-beige pb-4">
                    <p className="font-medium text-ink">{f.question}</p>
                    <p className="mt-1 text-sm text-ink-soft">{f.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <form data-testid="contact-form" onSubmit={submit} className="bg-cream-muted rounded-sm p-8 border border-beige h-fit">
          <div className="space-y-5">
            <input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nombre completo *" className="w-full bg-transparent border-b border-beige-dark py-3 focus:border-gold outline-none transition-colors" />
            <input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Correo electrónico *" className="w-full bg-transparent border-b border-beige-dark py-3 focus:border-gold outline-none transition-colors" />
            <input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Teléfono" className="w-full bg-transparent border-b border-beige-dark py-3 focus:border-gold outline-none transition-colors" />
            <input data-testid="contact-event" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} placeholder="Tipo de sesión / evento" className="w-full bg-transparent border-b border-beige-dark py-3 focus:border-gold outline-none transition-colors" />
            <textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Cuéntame tu idea *" rows={4} className="w-full bg-transparent border-b border-beige-dark py-3 focus:border-gold outline-none transition-colors resize-none" />
            <button data-testid="contact-submit" disabled={sending} className="w-full px-6 py-4 rounded-full bg-ink text-cream font-medium hover:bg-gold hover:text-ink transition-all duration-300 disabled:opacity-60">
              {sending ? "Enviando…" : "Enviar mensaje"}
            </button>
          </div>
        </form>
      </div>
    </Section>
  );
};
